import type { FormikContextType } from 'formik';

import type Enums from '@core/types/api/Enums';
import type { UserInfo } from '@core/types/api/UserInfo/UserInfo';
import type { LookupInputOption } from '@core/types/inputOption';
import { formatDecimalString, parseDecimalToNumber } from '@core/utils/decimals';
import {
  type PrimaryProductCategory,
  categoryTabs,
  getAvailableProductCategories,
} from '@core/utils/productCategoryFilter';

export enum MetricType {
  Volume = 'volume',
  Forecast = 'forecast',
  Backlog = 'backlog',
  Actual = 'actual',
  AtRisk = 'at-risk',
}

export type LocationPerformanceData = {
  category?: Enums.ProductCategory;
  unit: string;
  plannedVolume: string | null;
  variance: string | null;
  plannedActualPercentage: string | null;
  budgetUtilizationPercentage: string | null;
};

export type ProjectMonthlyBreakdownData = {
  category?: Enums.ProductCategory;
  unit: string;
  variance: string | null;
  actualVolume: string | null;
  backlogVolume: string | null;
  plannedVolume: string | null;
};

export type MetricItem = {
  value: string | null;
  unit?: string;
  status?: string;
  category?: Enums.ProductCategory;
};

export type BacklogFiltersFormikType = {
  project: { id: string; option: LookupInputOption }[];
  productCategory: PrimaryProductCategory | null;
  /** Selected market IDs. Empty means no market filter. */
  markets: string[];
  /** Selected salesperson (user) IDs. Empty means no salesperson filter. */
  salespersons: string[];
};

export enum ActualsTabs {
  DASHBOARD_OVERVIEW = 'Dashboard Overview',
  PROJECT_DETAILS = 'Project Details',
  CUSTOMER_PERFORMANCE = 'Customer Performance',
  ALERTS_AND_ACTIONS = 'Alerts & Actions',
}

export const tabs = [
  { label: ActualsTabs.DASHBOARD_OVERVIEW, value: '' },
  { label: ActualsTabs.PROJECT_DETAILS, value: 'project-details' },
  { label: ActualsTabs.CUSTOMER_PERFORMANCE, value: 'customer-performance' },
  { label: ActualsTabs.ALERTS_AND_ACTIONS, value: 'alerts' },
];

/**
 * Filter metrics based on the tenat's product categories.
 * @param metrics - The metrics to filter
 * @param userInfo - The user info
 * @returns The filtered metrics
 */
export const filterMetricsByTenantSettings = (
  metrics: MetricItem[],
  userInfo: UserInfo,
): MetricItem[] => {
  const tenantProductCategories = new Set<Enums.ProductCategory>(
    getAvailableProductCategories(userInfo.productCategories),
  );
  return metrics.filter(
    (item) => item.category !== undefined && tenantProductCategories.has(item.category),
  );
};

/**
 * Filter metrics based on the selected product category.
 * @param metrics - The metrics to filter
 * @param productCategory - The selected product category
 * @returns The filtered metrics
 */
export const filterMetricsBySelectedProductCategory = (
  metrics: MetricItem[],
  productCategory: PrimaryProductCategory | null,
): MetricItem[] => {
  if (productCategory === null) {
    return metrics;
  }
  return metrics.filter((item) => item.category === productCategory);
};

/**
 * Filter metrics based on the tenant's product categories and selected product category in the formik bag.
 * @param formikBag - The formik bag
 * @param userInfo - The user info
 * @returns The filtered metrics
 */
export const filterMetrics = (
  metrics: MetricItem[],
  formikBag: FormikContextType<BacklogFiltersFormikType>,
  userInfo: UserInfo,
): MetricItem[] => {
  const filtered = filterMetricsByTenantSettings(metrics, userInfo);
  return filterMetricsBySelectedProductCategory(filtered, formikBag.values.productCategory);
};

export const getFilteredCategoryTabsByTenantSettings = (
  userInfo: UserInfo,
): (typeof categoryTabs)[number][] => {
  return categoryTabs.filter((tab) => userInfo.productCategories.includes(tab.value));
};

export const overUnderVolume = (
  actualVolume: string | null,
  quotedVolume: string | null,
): string | null => {
  if (actualVolume === null && quotedVolume === null) {
    return null;
  }
  const formatted = formatDecimalString(
    parseDecimalToNumber(actualVolume) - parseDecimalToNumber(quotedVolume),
    2,
  );
  if (formatted === '' || formatted === '0' || formatted === '-0') {
    return null;
  }
  return formatted;
};
